import companiesService from '../services/companiesService.js';
import _ from 'lodash';
import userService from '../services/userService.js';
import dataModel from '../models/DataModel';
import DAO from './DAO.js';
import StoreItemProcessing from '../helpers/StoreFunctions/StoreItemProcessing';
import directoriesService from '../services/directoriesService';
import ReduxConstants, { RecordStates } from '../constants/ReduxConstants.js';
import { isInsightsOnlySession } from '../helpers/user/insightsRoleAccess';

export const company_root = {
  company: { additionalFields: ['logo'] }, vendor_users: {}, vendor_companies: { additionalFields: ['is_vendor_company'] },
};

export default class CompaniesDAO extends DAO {

  constructor(store) {
    super(store, 'company');
    for (let name of Object.getOwnPropertyNames(Object.getPrototypeOf(this))) {
      if (this[name] instanceof Function){
        this[name] = this[name].bind(this);
      }
    }
  }

  unsetCompany(id) {
    StoreItemProcessing.deleteItemInStore(this.itemType, id);
  }

  clearCompanies() {
    StoreItemProcessing.clearItemsInStore(this.itemType);
  }

  setCompanyReadOnly(company, readOnly) {
    company.readOnly = readOnly;
    let foundCompany = this.findItem(company.id);
    if (foundCompany) {
      foundCompany.readOnly = readOnly;
      this.setItemInStore(foundCompany);
    }
    else {
      foundCompany = StoreItemProcessing.findItem('vendor_company', company.id);
      if (foundCompany) {
        foundCompany.readOnly = readOnly;
        StoreItemProcessing.setItemInStore('vendor_companhy', foundCompany);
      }
    }
    return company;
  }

  getCompany(companyId, options, prefetchCallback) {
    if (companyId == 'admin_company') {
      return this.getAdminCompany(options);
    }
    else {
      return this._getItem(companyId, companiesService.getCompany.bind(this, companyId, options), options,
        null, null, prefetchCallback);
    }
  }

  async finishSetCurrentCompany(currentCompany, companyChanged) {
    localStorage.current_company_id = currentCompany.id;
    
    if (companyChanged) {

      if (localStorage.loggedin_user_id) {
        // Reget the current user, but for the new company so that permissions are right.
        let user = await dataModel.usersDAO.getUser(+localStorage.loggedin_user_id, { recordDescriptor: { user: { additionalFields: ['profile_image'], role: {}, companies: {}, company_vendor_users: {} } } });
        if (user) {
          user.settings.current_company_id = currentCompany.id;
          // Insights-only users cannot PATCH /users/:id (the API allowlists reads only for that
          // role) and have no company switcher, so there is no cross-session company to persist.
          // Keep the in-memory setting; skip the write that would 403.
          if (!isInsightsOnlySession()) {
            const updateUser = _.cloneDeep(user);
            dataModel.usersDAO.updateUser(updateUser);
          }
          dataModel.usersDAO.initializePermissions(user, currentCompany);
          if (!user.super_admin && user.companies && user.companies.length && user.companies[0].id != currentCompany.id) {
            user.isVendorUser = true;
          }
          dataModel.usersDAO.currentUser = user;
          dataModel.resetActionCable();
        }
      }

      // GET /roles is denied to insights-only users, and the role list only feeds user-management
      // UI they never reach.
      if (!isInsightsOnlySession()) {
        userService.getUserRoles(currentCompany.id).then(roles => {
          dataModel.usersDAO.userRoles = roles;
        });
      }
    }
    else {
      dataModel.collectionsDAO.markCollectionNeedsSync();
      dataModel.AnalyticsDAO.markTaskAnalyticsNeedsSync();
    }
  
    dataModel.displayCompanyBanners();
  }

  get currentCompany() {
    return this.stateTree.new_companies.currentCompany;
  }

  get adminCompany() {
    let ac = this.stateTree.new_companies.adminCompany;
    if (ac) {
      return this.mapToReturn(ac, { company: { directories: { users: {} } } });
    }
  }

  getCurrentCompany(company_id) {
    if (company_id < 0) {
      for (let key in this.stateTree.new_companies) {
        if (!Number.isNaN(+key) && parseInt(key, 10) * -1 != company_id && parseInt(key, 10) != 1) {
          company_id = +key;
          break;
        }
      }
    }
    if (company_id != +localStorage.current_company_id) {
      localStorage.current_company_id = company_id;
      dataModel.storeManager.reset(localStorage.loggedin_user_id, false, {
        ignoreCollections: false, newCompany: true, toIgnore: { users: [dataModel.usersDAO.currentUser] },
        newUser: false, clearAll: false, clearDeprecated: true,
      });
      dataModel.PageStateDAO.clearState({});
    }
    const self = this;
    return this.getCompany(company_id, { recordDescriptor: { company: { additionalFields: ['logo'] }, vendor_users: {} } }).then(company => {
      if (company && !company.error && !company.offline) {
        self.setCurrentCompany(company);
        if (!self.stateTree.new_companies.adminCompany) {
          self.getAdminCompany({ recordDescriptor: { company: { directories: { users: {} } } } }).then(adminCompany => {
            if (adminCompany) {
              self.store.dispatch({
                type: 'ADMIN_COMPANY_SET',
                adminCompany: adminCompany,
              });
            }
          });
        }
      }
      return company;
    });
  }

  setCurrentCompany(company) {
    let oldCurrentCompany = this.stateTree.new_companies.currentCompany;
    let currentCompany = this.findItem(company.id);
    if (currentCompany && (!oldCurrentCompany || oldCurrentCompany.id != currentCompany.id)) {
      // store current company denormalized
      this.store.dispatch({
        type: 'CURRENT_COMPANY_SET',
        currentCompany: company,
      });
      this.finishSetCurrentCompany(company, !oldCurrentCompany || oldCurrentCompany.id != currentCompany.id);
    }
  }

  getAdminCompany(options) {
    // GET /admin/companies is denied to insights-only users. Callers already treat a falsy admin
    // company as "not available" (the request also resolves undefined on error), so short-circuit
    // rather than issue a request that can only 403.
    if (isInsightsOnlySession()) {
      return Promise.resolve(null);
    }

    return companiesService.getAdminCompany(options).then(response => {
      if (!response.error && !response.offline) {
        const storeItem = this.normalizeAndSyncToStore(response, { store: true });
        return Promise.resolve(this.mapToReturn(storeItem, options));
      }
    });
  }

  generateAPIKeyForCurrentCompany() {
    return companiesService.updateAPIKey(this.currentCompany.id)
      .then(response => {
        const storeItem = this.normalizeAndSyncToStore(response, { store: true });
        return Promise.resolve(this.mapToReturn(storeItem));
      });
  }
  
  clearState(options) {
    if (options.toIgnore.companies) {
      options.toIgnore.companies.push('currentCompany');
    }
    this.clearMyState(this.stateTree.new_companies, options.toIgnore.companies, this.unsetCompany, this.clearCompanies, options);
  }

  handleResponse(company, response, dontQueue, options, errorMessage) {
    if (response.offline && dontQueue) {
      return response;    
    }
    else if (response.offline) {
      this.throwItemError();
    }
    else if (response.error) {
      dataModel.displayHTTPError(errorMessage, response.error, false);
      this.markError(company);
      this.throwItemError();
    }
    else {
      this.normalizeAndSyncToStore(response, options);
      const denormalized = this.mapToReturn(this.findItem(response.data.id), options);
      if (errorMessage == 'Creating a company') {
        const directory = { name: denormalized.name, notes: 'Directory for ' + denormalized.name, company_id: denormalized.id };
        return directoriesService.createDirectory(directory, denormalized.id, true).then(newDirectory => {
          return this.getCompany(denormalized.id, options).then(updatedCompany => {
            return updatedCompany;
          });
        });
      }
      else {
        if (company.id == this.currentCompany.id) {
          this.setCurrentCompany(denormalized);
        }
        return denormalized;
      }
    }
  }

  createCompany(company, options) {
    return companiesService.createCompany(company, null, options).then(response => {
      return this.handleResponse(company, response, false, options, 'Creating a company');
    });
  }

  createVendorCompany(vendorCompany) {
    return companiesService.createVendorCompany(vendorCompany).then(newVendor => {
      if (newVendor.error) {
        dataModel.displayHTTPError('Creating a vendor company',  newVendor.error);
      }
      else {
        dataModel.displayInfoErrorMessage('The vendor company was added successfully!', 4000, 1);
      }
      return newVendor;
    });
  }

  deleteVendorCompany(vendorCompany) {
    return companiesService.deleteVendorCompany(vendorCompany).then(response => {
      if (response.error) {
        dataModel.displayHTTPError('Deleting a vendor company', response.error);
      }
      else {
        dataModel.displayInfoErrorMessage('The vendor company was removed successfully!', 4000, 1);
      }
    });
  }

  updateCompany(company, options) {
    if (company.id == this.currentCompany.id) {
      this.store.dispatch({
        type: 'CURRENT_COMPANY_SET',
        currentCompany: company,
      });
    }
    return companiesService.updateCompany(company, null, options).then(response => {
      return this.handleResponse(company, response, false, options, 'Updating a company');
    });
  }

  deleteCompany(company) {
    if (typeof company == 'number') {
      company = this.findItem(company);
    }
    if (company) {
      this.markDeleted(company);
      return companiesService.deleteCompanyAdmin(company.id).then(response => {
        return response;
      });
    }
  }

  checkCompaniesCollections(company_id) {
    let collectionIds = dataModel.collectionsDAO.getCollectionsByType(ReduxConstants.COMPANY);
    for (let collectionId of collectionIds) {
      let collection = dataModel.collectionsDAO.findCollection(collectionId);

      if (collection && !collection.ids.find(id => {return id == company_id;})){
        collection._recordState = RecordStates.NEEDS_SYNC;
        dataModel.collectionsDAO.setCollection(collectionId, collection, ReduxConstants.COMPANY);
      }
    }
  }

  handleActionCableAndGetRequest(acData) {  
    let company = this.findItem(acData.company_id);
    if (company) {
      if (acData.is_attribute_update) {
        this.markNeedsAttributeSync(company);
      }
      else {
        this.markNeedsSync(company);
      }
    }
    this.checkCompaniesCollections(acData.company_id);
    switch (acData.type) {
    case 'newCompany':
    case 'updateCompany':
    case 'createMarket':
    case 'updateMarket':
    case 'createCustomer':
      return this.getCompany.bind(this);
    case 'deleteCompany':
      this.unsetCompany(acData.company_id);
      return company;
    default:
    }

  }

}



// WEBPACK FOOTER //
// ./src/dao/CompaniesDAO.js